import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, ChevronDown } from "lucide-react";
import DatePicker from "react-datepicker";
import { useAuth } from "@clerk/clerk-react";
import dayjs from "dayjs";
import "react-datepicker/dist/react-datepicker.css";
import { getDigestListService } from "../../utils/digestService";
import { setDigestList } from "../../reducers/digestReducer";
import "../../components/Digest/DigestDatePicker.css";

const DigestDatePicker = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getDigestListCalled = useRef(false);
  const { userId } = useAuth();

  // Ref for the whole date picker component
  const containerRef = useRef(null);

  // Get digest date from URL
  const { digestDate } = useParams();

  // Get digest list and current digest from Redux store
  const digestList = useSelector(state => state.digestList.digests);
  const currentDigest = useSelector(state => state.digestList.currentDigest);

  const [digestListIsLoaded, setDigestListIsLoaded] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (currentDigest?.title) {
      setSelectedDate(new Date(currentDigest.title));
    }
  }, [currentDigest?.title]);

  // Extract dates from digest titles and convert to dayjs objects
  const getAvailableDates = () => {
    if (!digestList || digestList.length === 0) return [];

    return digestList
      .map(digest => {
        if (!digest.title) return null;

        // Parse title like "September 12, 2025"
        const dateMatch = digest.title.match(/(\w+)\s+(\d+),\s+(\d{4})/);
        if (!dateMatch) return null;

        const [, month, day, year] = dateMatch;
        const monthNames = {
          'January': 0, 'February': 1, 'March': 2, 'April': 3,
          'May': 4, 'June': 5, 'July': 6, 'August': 7,
          'September': 8, 'October': 9, 'November': 10, 'December': 11
        };

        const monthIndex = monthNames[month];
        if (monthIndex === undefined) return null;

        const date = new Date(year, monthIndex, parseInt(day));
        return dayjs(date);
      })
      .filter(date => date !== null);
  };

  // Function to check if a date should be disabled
  const isDateDisabled = (date) => {
    const availableDates = getAvailableDates();
    if (availableDates.length === 0) return true;

    const dayjsDate = dayjs(date);
    return availableDates.some(availableDate =>
      availableDate.isSame(dayjsDate, 'day')
    );
  };

  useEffect(() => {
    const fetchDigestList = async () => {
      if (getDigestListCalled.current) return;
      getDigestListCalled.current = true;

      try {
        const digestList = await getDigestListService(userId);
        dispatch(setDigestList(digestList));
        setDigestListIsLoaded(true);
      } catch (error) {
        console.error('Error getting digest list:', error);
      } finally {
        getDigestListCalled.current = false;
      }
    }
    if (!digestListIsLoaded) {
      fetchDigestList();
    }
  }, [userId, digestListIsLoaded, getDigestListCalled, dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
    if (date) {
      const isoDate = date.toISOString().slice(0, 10); // YYYY-MM-DD
      navigate(`/digest/${isoDate}`);
    }
  };

  // Hide date picker if click outside
  const handleClickOutside = useCallback(
    (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    },
    [setShowDatePicker]
  );

  useEffect(() => {
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    }
    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showDatePicker, handleClickOutside]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        className={`flex flex-row items-center font-semibold text-slate-600 p-2 m-0 gap-4 bg-transparent hover:bg-sky-500 hover:text-white rounded-lg`}
        onClick={() => setShowDatePicker(!showDatePicker)}
        data-amp-track-section="Sidebar"
        data-amp-track-action="Date_Picker_Toggle"
        data-amp-track-description="Toggle Date Picker"
        type="button"
      >
        <Calendar size={20} />
        <span className="flex flex-row items-center gap-2 text-lg">
          {(digestDate && selectedDate)
            ? dayjs(selectedDate).format('MMMM D, YYYY')
            : currentDigest?.title || 'Daily Digest'
          } <ChevronDown size={20} />
        </span>
      </button>

      {showDatePicker && (
        <div
          className="absolute left-0 z-50 mt-2 bg-white rounded-lg shadow-lg border border-slate-200"
          style={{ top: '100%' }}
        >
          <div className="px-2 pt-2">
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              inline
              calendarClassName="react-datepicker-custom"
              filterDate={isDateDisabled}
              excludeDates={[]} // We're using filterDate instead
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DigestDatePicker;